/**
 * Archivos adjuntos (pedido de la dueña 2026-10-19; decisión «Archivos adjuntos manuales en general»): la lista de los
 * archivos de una cosa (compra, gasto, retención, reintegro, cliente o documento) con su nombre, tipo, tamaño, quién y
 * cuándo; «Adjuntar archivo» (foto o PDF; en el teléfono deja tomar la foto con la cámara) y verlo en una ventana o en
 * otra pestaña. El archivo se pide con el token y se muestra desde un blob: no hay dirección pública. «Borrar» es solo
 * de la dueña y pide el motivo. Quién ve y quién sube lo dice PERMISO_ADJUNTO (el servidor decide igual).
 */
import {
  ADJUNTO_MAX_BYTES,
  CATEGORIA_ADJUNTO_SUGERIDA,
  mensajeArchivoMuyGrande,
  PERMISO_ADJUNTO,
  tamanoLegible,
  TIPO_ADJUNTO_ETIQUETA,
  TIPOS_ADJUNTO,
  type AdjuntoDTO,
  type EntidadAdjunto,
  type TipoAdjunto,
} from '@mg/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ExternalLink, FileText, ImageIcon, Paperclip, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Textarea } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { get, getToken, mensajeDeError, post, refrescarToken } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { cn, formatearFechaHora } from '@/lib/utils.ts';

// ─── Datos ──────────────────────────────────────────────────────────────────
export const clavesAdjuntos = {
  todo: ['adjuntos'] as const,
  lista: (entidad: EntidadAdjunto, id: string) => ['adjuntos', 'lista', entidad, id] as const,
  conteo: (entidad: EntidadAdjunto, ids: readonly string[]) => ['adjuntos', 'conteo', entidad, ids.join(',')] as const,
};

export const useAdjuntos = (entidad: EntidadAdjunto, id: string, habilitado = true) =>
  useQuery({ queryKey: clavesAdjuntos.lista(entidad, id), queryFn: () => get<AdjuntoDTO[]>('/adjuntos', { entidad, entidad_id: id }), enabled: habilitado && !!id });

/** Cuántos archivos tiene cada una de varias cosas (p. ej. las filas de una tabla): { id: cantidad }. */
export function useConteoAdjuntos(entidad: EntidadAdjunto, ids: readonly string[]) {
  const { puede } = useAuth();
  const veo = puede(PERMISO_ADJUNTO[entidad].ver, 'lectura');
  return useQuery({ queryKey: clavesAdjuntos.conteo(entidad, ids), queryFn: () => get<Record<string, number>>('/adjuntos/conteo', { entidad, ids: ids.join(',') }), enabled: veo && ids.length > 0 });
}

const leerBase64 = (archivo: Blob) =>
  new Promise<string>((resolver, rechazar) => {
    const lector = new FileReader();
    lector.addEventListener('load', () => resolver(String(lector.result).split(',')[1] ?? ''));
    lector.addEventListener('error', () => rechazar(new Error('No se pudo leer el archivo')));
    lector.readAsDataURL(archivo);
  });

const cargarImagen = (url: string) =>
  new Promise<HTMLImageElement>((resolver, rechazar) => {
    const img = new Image();
    img.addEventListener('load', () => resolver(img), { once: true });
    img.addEventListener('error', () => rechazar(new Error('No se pudo leer la foto: adjunta un JPG, PNG o WEBP, o un PDF')), { once: true });
    img.src = url;
  });

/**
 * Una foto que pasa de este tamaño se achica antes de enviarla (decisión del 2026-10-27, D2 de la lista del 22-10): las
 * del teléfono pesan varios MB y llenaban el servidor. Queda en unos 200 a 400 KB y la factura se sigue leyendo.
 */
export const FOTO_LIVIANA_BYTES = 600 * 1024;

/** Si una foto se achica antes de guardarla: las de más de 600 KB y las de un formato que no se guarda (como HEIC). */
export const fotoSeAchica = (tipo: string, bytes: number) => tipo.startsWith('image/') && (!(TIPOS_ADJUNTO as readonly string[]).includes(tipo) || bytes > FOTO_LIVIANA_BYTES);

/**
 * La foto se vuelve un JPG liviano, con buena resolución para que la factura o el comprobante se sigan leyendo: busca
 * quedar en 600 KB o menos; si una foto con mucho detalle no baja, vale la más liviana que no pase de 3 MB.
 */
async function fotoAJpg(archivo: File): Promise<Blob> {
  const url = URL.createObjectURL(archivo);
  try {
    const img = await cargarImagen(url);
    let mejor: Blob | null = null;
    for (const [lado, calidad] of [[2000, 0.8], [1600, 0.75], [1400, 0.7], [1200, 0.65]] as const) {
      const escala = Math.min(1, lado / Math.max(img.naturalWidth, img.naturalHeight));
      const lienzo = document.createElement('canvas');
      lienzo.width = Math.max(1, Math.round(img.naturalWidth * escala));
      lienzo.height = Math.max(1, Math.round(img.naturalHeight * escala));
      const ctx = lienzo.getContext('2d');
      if (!ctx) throw new Error('Este navegador no puede preparar la foto');
      ctx.drawImage(img, 0, 0, lienzo.width, lienzo.height);
      const blob = await new Promise<Blob | null>((r) => lienzo.toBlob(r, 'image/jpeg', calidad));
      if (blob && blob.size <= FOTO_LIVIANA_BYTES) return blob;
      if (blob && (!mejor || blob.size < mejor.size)) mejor = blob;
    }
    if (mejor && mejor.size <= ADJUNTO_MAX_BYTES) return mejor;
    throw new Error('La foto sigue pesando más de 3 MB: tómala de nuevo con menos resolución');
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** El archivo elegido, listo para enviar: tipo permitido, hasta 3 MB, en base64. */
export async function prepararArchivo(archivo: File): Promise<{ nombre: string; tipo: TipoAdjunto; contenido: string }> {
  const permitido = (TIPOS_ADJUNTO as readonly string[]).includes(archivo.type);
  let nombre = archivo.name || 'archivo';
  let tipo = archivo.type as TipoAdjunto;
  let datos: Blob = archivo;
  if (fotoSeAchica(archivo.type, archivo.size)) {
    const jpg = await fotoAJpg(archivo);
    // Si la foto ya se podía guardar y achicarla no la hace más liviana, va la original
    if (permitido && jpg.size >= archivo.size) datos = archivo;
    else {
      datos = jpg;
      tipo = 'image/jpeg';
      nombre = `${nombre.replace(/\.[^.]+$/, '')}.jpg`;
    }
  } else if (!permitido) {
    throw new Error(`«${nombre}» no se puede adjuntar: solo fotos (JPG, PNG o WEBP) o PDF`);
  }
  if (datos.size > ADJUNTO_MAX_BYTES) throw new Error(mensajeArchivoMuyGrande(nombre, datos.size));
  return { nombre, tipo, contenido: await leerBase64(datos) };
}

/** El archivo pedido con el token (renovándolo una vez si venció), como dirección de blob para verlo. */
export async function abrirAdjunto(id: string): Promise<string> {
  const pedir = (token: string | null) => fetch(`/api/adjuntos/${id}/archivo`, { headers: { authorization: `Bearer ${token ?? ''}` }, credentials: 'include' });
  let res = await pedir(getToken());
  if (res.status === 401) {
    const nuevo = await refrescarToken();
    if (nuevo) res = await pedir(nuevo);
  }
  if (!res.ok) {
    const cuerpo = (await res.json().catch(() => null)) as { error?: { mensaje?: string } } | null;
    throw new Error(cuerpo?.error?.mensaje ?? `No se pudo abrir el archivo (${res.status})`);
  }
  return URL.createObjectURL(await res.blob());
}

// ─── Componente ─────────────────────────────────────────────────────────────
export interface PropsAdjuntos {
  entidad: EntidadAdjunto;
  entidadId: string;
  /** Título de la sección (por defecto «Archivos adjuntos»). */
  titulo?: string;
  /** Categoría que se guarda con lo que se adjunte aquí (por defecto la sugerida para esa cosa). */
  categoria?: string;
  /** Solo ver: sin «Adjuntar archivo» aunque el usuario pueda subir. */
  soloLectura?: boolean;
  /** Texto cuando no hay archivos. */
  vacio?: string;
  className?: string;
}

export function Adjuntos({ entidad, entidadId, titulo = 'Archivos adjuntos', categoria, soloLectura = false, vacio = 'Sin archivos adjuntos.', className }: PropsAdjuntos) {
  const { puede, usuario } = useAuth();
  const qc = useQueryClient();
  const veo = puede(PERMISO_ADJUNTO[entidad].ver, 'lectura');
  const subo = !soloLectura && puede(PERMISO_ADJUNTO[entidad].subir);
  const duena = usuario?.rol === 'superadmin';
  const lista = useAdjuntos(entidad, entidadId, veo);
  const [viendo, setViendo] = useState<AdjuntoDTO | null>(null);
  const [borrando, setBorrando] = useState<AdjuntoDTO | null>(null);
  const archivo = useRef<HTMLInputElement>(null);
  const camara = useRef<HTMLInputElement>(null);
  const subir = useMutation({
    mutationFn: async (f: File) => post<AdjuntoDTO>('/adjuntos', { entidad, entidad_id: entidadId, categoria: categoria ?? CATEGORIA_ADJUNTO_SUGERIDA[entidad], ...(await prepararArchivo(f)) }),
    onSuccess: (a) => {
      toast.success(`Archivo adjuntado: «${a.nombre}»`, { description: 'Queda guardado en el sistema y en la auditoría.' });
      void qc.invalidateQueries({ queryKey: clavesAdjuntos.todo });
    },
    onError: (e) => toast.error('No se pudo adjuntar el archivo', { description: mensajeDeError(e) }),
  });
  if (!veo) return null;
  const elegir = async (entrada: HTMLInputElement | null) => {
    const archivos = [...(entrada?.files ?? [])];
    if (entrada) entrada.value = '';
    for (const f of archivos) await subir.mutateAsync(f).catch(() => undefined);
  };
  const filas = lista.data ?? [];
  return (
    <div className={cn('space-y-2', className)} data-testid={`adjuntos-${entidad}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1 text-sm font-medium"><Paperclip className="h-4 w-4 text-muted-foreground" /> {titulo}{filas.length > 0 ? <span className="rounded-full bg-muted px-1.5 text-[11px]">{filas.length}</span> : null}</p>
        {subo ? (
          <div className="flex gap-1">
            <input ref={archivo} type="file" multiple accept="image/jpeg,image/png,image/webp,image/*,application/pdf" className="hidden" onChange={(e) => void elegir(e.currentTarget)} data-testid={`adjuntos-input-${entidad}`} />
            <input ref={camara} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => void elegir(e.currentTarget)} data-testid={`adjuntos-camara-${entidad}`} />
            <Button size="sm" variant="outline" cargando={subir.isPending} onClick={() => archivo.current?.click()} data-testid={`adjuntar-${entidad}`}><Paperclip /> Adjuntar archivo</Button>
            <Button size="sm" variant="ghost" disabled={subir.isPending} onClick={() => camara.current?.click()} aria-label="Tomar una foto con la cámara" title="Tomar una foto con la cámara (en el teléfono)" data-testid={`adjuntar-foto-${entidad}`}><Camera /></Button>
          </div>
        ) : null}
      </div>
      {lista.isLoading ? <p className="text-xs text-muted-foreground">Cargando archivos…</p> : lista.error ? (
        <p className="text-xs text-mg-rojo">No se pudieron cargar los archivos: {mensajeDeError(lista.error)} <button className="underline" onClick={() => void lista.refetch()}>Reintentar</button></p>
      ) : filas.length === 0 ? <p className="text-xs text-muted-foreground" data-testid={`adjuntos-vacio-${entidad}`}>{vacio}{subo ? ' Foto o PDF, hasta 3 MB.' : ''}</p> : (
        <ul className="divide-y rounded-lg border text-sm" data-testid={`adjuntos-lista-${entidad}`}>
          {filas.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 px-2 py-1.5" data-testid={`adjunto-${a.nombre}`}>
              <span className="flex min-w-0 items-start gap-2">
                {a.tipo === 'application/pdf' ? <FileText className="mt-0.5 h-4 w-4 shrink-0 text-mg-rojo" /> : <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 text-mg-verde" />}
                <span className="min-w-0">
                  <button className="break-all text-left font-medium underline-offset-2 hover:underline" onClick={() => setViendo(a)} data-testid={`adjunto-ver-${a.nombre}`}>{a.nombre}</button>
                  <span className="block text-[11px] text-muted-foreground">{TIPO_ADJUNTO_ETIQUETA[a.tipo]} · {tamanoLegible(a.tamano)}{a.categoria ? ` · ${a.categoria}` : ''} · subió {a.subido_por_nombre} el {formatearFechaHora(a.subido_en)}</span>
                </span>
              </span>
              <span className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setViendo(a)}>Ver</Button>
                {duena ? <Button size="sm" variant="ghost" className="text-mg-rojo" onClick={() => setBorrando(a)} data-testid={`adjunto-borrar-${a.nombre}`}><Trash2 /> Borrar</Button> : null}
              </span>
            </li>
          ))}
        </ul>
      )}
      {viendo ? <VisorAdjunto adjunto={viendo} cerrar={() => setViendo(null)} /> : null}
      {borrando ? <BorrarAdjunto adjunto={borrando} cerrar={() => setBorrando(null)} /> : null}
    </div>
  );
}

/** Ver el archivo en una ventana (la foto o el PDF), con la opción de abrirlo en otra pestaña. */
function VisorAdjunto({ adjunto, cerrar }: { adjunto: AdjuntoDTO; cerrar: () => void }) {
  const archivo = useQuery({ queryKey: ['adjuntos', 'archivo', adjunto.id], queryFn: () => abrirAdjunto(adjunto.id), staleTime: Infinity, gcTime: 0, retry: false });
  const url = archivo.data;
  // La dirección del blob se libera al cerrar (con margen por si se abrió en otra pestaña)
  useEffect(() => () => { if (url) setTimeout(() => URL.revokeObjectURL(url), 60_000); }, [url]);
  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-h-[94vh] max-w-4xl overflow-y-auto" data-testid="adjunto-visor">
        <DialogHeader>
          <DialogTitle className="break-all pr-6">{adjunto.nombre}</DialogTitle>
          <DialogDescription>{TIPO_ADJUNTO_ETIQUETA[adjunto.tipo]} · {tamanoLegible(adjunto.tamano)}{adjunto.categoria ? ` · ${adjunto.categoria}` : ''} · subió {adjunto.subido_por_nombre} el {formatearFechaHora(adjunto.subido_en)}</DialogDescription>
        </DialogHeader>
        {archivo.isLoading ? <p className="text-sm text-muted-foreground">Abriendo el archivo…</p> : archivo.error ? <p className="text-sm text-mg-rojo">{mensajeDeError(archivo.error)}</p> : url ? (
          adjunto.tipo === 'application/pdf'
            ? (
              <object data={url} type="application/pdf" aria-label={adjunto.nombre} className="h-[70vh] w-full rounded-lg border" data-testid="adjunto-visor-pdf">
                <p className="p-4 text-sm text-muted-foreground">Este navegador no muestra el PDF dentro de la ventana: usa «Abrir en otra pestaña».</p>
              </object>
            )
            : <img src={url} alt={adjunto.nombre} className="mx-auto max-h-[70vh] max-w-full rounded-lg border object-contain" />
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={!url} onClick={() => url && window.open(url, '_blank', 'noopener')} data-testid="adjunto-pestana"><ExternalLink /> Abrir en otra pestaña</Button>
          <Button onClick={cerrar}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Borrar (solo la dueña): pide el motivo; el archivo deja de verse y queda en la auditoría. */
function BorrarAdjunto({ adjunto, cerrar }: { adjunto: AdjuntoDTO; cerrar: () => void }) {
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const borrar = useMutation({
    mutationFn: () => post<{ id: string; borrado: true }>(`/adjuntos/${adjunto.id}/borrar`, { motivo: motivo.trim() }),
    onSuccess: () => { toast.success(`Archivo borrado: «${adjunto.nombre}»`, { description: 'Ya no se ve; queda en la auditoría con tu motivo.' }); void qc.invalidateQueries({ queryKey: clavesAdjuntos.todo }); cerrar(); },
    onError: (e) => toast.error('No se pudo borrar el archivo', { description: mensajeDeError(e) }),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className="max-w-md" data-testid="adjunto-borrar-dialogo">
        <DialogHeader>
          <DialogTitle>Borrar archivo adjunto</DialogTitle>
          <DialogDescription>«{adjunto.nombre}» ({TIPO_ADJUNTO_ETIQUETA[adjunto.tipo]}, {tamanoLegible(adjunto.tamano)}), subido por {adjunto.subido_por_nombre}. Deja de verse aquí; en la auditoría queda quién lo borró, cuándo y por qué. No se puede deshacer.</DialogDescription>
        </DialogHeader>
        <Campo etiqueta="Motivo" htmlFor="adjunto-motivo" ayuda="Al menos 5 letras"><Textarea id="adjunto-motivo" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. se adjuntó en la compra equivocada" data-testid="adjunto-borrar-motivo" /></Campo>
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>Cancelar</Button>
          <Button variant="destructive" disabled={motivo.trim().length < 5} cargando={borrar.isPending} onClick={() => borrar.mutate()} data-testid="adjunto-borrar-confirmar"><Trash2 /> Borrar archivo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
